import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useResidentTransactions } from '@/hooks/admin/useResidentTransactions';
import { useResponsive } from '@/hooks/common/useResponsive';
import { getApiErrorDescription } from '@/utils/admin/errorHandling';
import { TransactionSummary } from './TransactionSummary';
import { TransactionTable } from '../desktop/TransactionTable';
import { TransactionList } from '../mobile/TransactionList';
import { TransactionHistoryModal as DesktopTransactionHistoryModal } from '../desktop/TransactionHistoryModal';
import { TransactionHistoryModal as MobileTransactionHistoryModal } from '../mobile/TransactionHistoryModal';

interface TransactionHistorySectionProps {
  accountId?: number;
  leaseId: number;
  residentName?: string;
}

export const TransactionHistorySection: React.FC<TransactionHistorySectionProps> = ({ 
  accountId, 
  leaseId,
  residentName,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { showFullTable } = useResponsive();
  
  const { transactions, summary, isLoading, error } = useResidentTransactions({
    accountId: accountId || 0,
    leaseId,
    autoFetch: true,
  });

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // Don't render if no accountId
  if (!accountId) {
    return null;
  }

  return (
    <View style={styles.section}>
      {/* Section Header */}
      <TouchableOpacity 
        style={styles.header}
        onPress={handleToggleExpand}
        activeOpacity={0.7}
      >
        <Text style={styles.sectionTitle}>Transaction History</Text>
        <View style={styles.expandIcon}>
          {isExpanded ? (
            <SVG_COMPONENTS.CHEVRON_DOWN width={16} height={16} color={ADMIN_COLORS.BACKGROUND.INPUT}/>
          ) : (
            <SVG_COMPONENTS.CHEVRON_RIGHT width={16} height={16} color={ADMIN_COLORS.BACKGROUND.INPUT}/>
          )}
        </View>
      </TouchableOpacity>

      {/* Loading State */}
      {isLoading && (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="small" color={ADMIN_COLORS.PRIMARY} />
          <Text style={styles.loadingText}>Loading transactions...</Text>
        </View>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>
            {getApiErrorDescription(error, 'Unable to load transaction history. Please try again later.')}
          </Text>
        </View>
      )}

      {/* Summary (always visible when not loading/error) */}
      {!isLoading && !error && (
        <TransactionSummary summary={summary} />
      )}

      {/* Expanded Content */}
      {isExpanded && !isLoading && !error && (
        <View style={styles.expandedContent}>
          {transactions.length === 0 ? (
            <View style={styles.emptyState}>
              <Text style={styles.emptyStateText}>
                No transactions found for this resident.
              </Text>
            </View>
          ) : (
            <>
              {/* Transaction List/Table */}
              <View style={styles.transactionsContainer}>
                {showFullTable ? (
                  <TransactionTable 
                    transactions={transactions} 
                    maxRows={10}
                  />
                ) : (
                  <TransactionList 
                    transactions={transactions} 
                    maxRows={5}
                  />
                )}
              </View>

              {/* View All Button */}
              {transactions.length > (showFullTable ? 10 : 5) && (
                <TouchableOpacity 
                  style={styles.viewAllButton}
                  onPress={() => setShowModal(true)}
                >
                  <Text style={styles.viewAllText}>
                    View All {transactions.length} Transactions
                  </Text>
                </TouchableOpacity>
              )}
            </>
          )}
        </View>
      )}

      {/* Full Transaction History Modal */}
      {showFullTable ? (
        <DesktopTransactionHistoryModal
          visible={showModal}
          onClose={() => setShowModal(false)}
          transactions={transactions}
          residentName={residentName}
        />
      ) : (
        <MobileTransactionHistoryModal
          visible={showModal}
          onClose={() => setShowModal(false)}
          transactions={transactions}
          residentName={residentName}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  section: {
    backgroundColor: ADMIN_COLORS.SURFACE,
    paddingHorizontal: 16,
    paddingVertical: 20,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  expandIcon: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 20,
    gap: 8,
  },
  loadingText: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  errorContainer: {
    paddingVertical: 12,
  },
  errorText: {
    fontSize: 14,
    color: ADMIN_COLORS.ERROR,
    textAlign: 'center',
  },
  expandedContent: {
    marginTop: 8,
  },
  transactionsContainer: {
    maxHeight: 500,
    marginBottom: 12,
  },
  emptyState: {
    paddingVertical: 32,
    alignItems: 'center',
  },
  emptyStateText: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    textAlign: 'center',
  },
  viewAllButton: {
    backgroundColor: ADMIN_COLORS.PRIMARY,
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 6,
    alignItems: 'center',
    marginTop: 8,
  },
  viewAllText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
});

